/**
 * General timeline from public health sources (WHO, "Health benefits of smoking cessation").
 * Shown as encouragement with a "not medical advice" line, never as a promise.
 */
export interface HealthMilestone {
  key: string;
  afterMinutes: number;
  label: string;
  text: string;
}

const H = 60;
const D = 24 * H;
const Y = 365 * D;

export const HEALTH_MILESTONES: readonly HealthMilestone[] = [
  {
    key: '20m',
    afterMinutes: 20,
    label: '20 minutes',
    text: 'Heart rate and blood pressure begin to drop.',
  },
  {
    key: '12h',
    afterMinutes: 12 * H,
    label: '12 hours',
    text: 'Carbon monoxide in the blood drops toward normal.',
  },
  {
    key: '2w',
    afterMinutes: 14 * D,
    label: '2 weeks',
    text: 'Circulation and lung function begin to improve over the next few months.',
  },
  {
    key: '1mo',
    afterMinutes: 30 * D,
    label: '1 month',
    text: 'Coughing and shortness of breath often ease over the next several months.',
  },
  {
    key: '1y',
    afterMinutes: Y,
    label: '1 year',
    text: 'The added risk of heart disease is about half that of someone who smokes.',
  },
  {
    key: '5y',
    afterMinutes: 5 * Y,
    label: '5 years',
    text: 'Stroke risk can fall toward that of someone who never smoked.',
  },
  {
    key: '10y',
    afterMinutes: 10 * Y,
    label: '10 years',
    text: 'The risk of lung cancer is about half that of someone who smokes.',
  },
];

export function milestoneProgress(minutesSince: number): {
  reached: HealthMilestone[];
  next: HealthMilestone | null;
  minutesToNext: number | null;
} {
  const reached = HEALTH_MILESTONES.filter((m) => m.afterMinutes <= minutesSince);
  const next = HEALTH_MILESTONES.find((m) => m.afterMinutes > minutesSince) ?? null;
  return { reached, next, minutesToNext: next ? next.afterMinutes - minutesSince : null };
}
